import React from 'react';

type StylesMap = Record<string, any>;

export type MissedCauseCandidate = {
  id: string;
  shortTitle: string;
  description: string;
  component?: string | null;
  domain?: string | null;
  probability?: 'HIGH' | 'MEDIUM' | 'LOW';
  failureMode?: string;
  reasoningSummary: string;
  sourceNodeIds?: string[];
};

export type MissedCauseCheckResult = {
  outcome: 'CANDIDATES_FOUND' | 'NO_CANDIDATES';
  summary: string;
  candidates: MissedCauseCandidate[];
  validationIssues?: string[];
};

export type MissedCauseBatchResult = {
  candidateId: string;
  shortTitle: string;
  outcome: 'ADDED' | 'DUPLICATE' | 'NOT_ADDED' | 'FAILED';
  message: string;
};

type TSFADMissedCausesModalProps = {
  open: boolean;
  styles: StylesMap;
  loading: boolean;
  error: string | null;
  workflowError: string | null;
  optimizationComplete: boolean;
  result: MissedCauseCheckResult | null;
  selectedCandidateIds: string[];
  processing: boolean;
  optimizing: boolean;
  processingCandidateId: string | null;
  batchResults: MissedCauseBatchResult[];
  onClose: () => void;
  onRetry: () => void;
  onToggleCandidate: (candidateId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onAddSelected: () => void;
};

export const TSFADMissedCausesModal: React.FC<TSFADMissedCausesModalProps> = ({
  open,
  styles,
  loading,
  error,
  workflowError,
  optimizationComplete,
  result,
  selectedCandidateIds,
  processing,
  optimizing,
  processingCandidateId,
  batchResults,
  onClose,
  onRetry,
  onToggleCandidate,
  onSelectAll,
  onClearSelection,
  onAddSelected
}) => {
  if (!open) return null;

  const candidates = result?.candidates ?? [];
  const selectedIds = new Set(selectedCandidateIds);
  const resultByCandidateId = new Map(batchResults.map(item => [item.candidateId, item]));
  const addedCount = batchResults.filter(item => item.outcome === 'ADDED').length;
  const coveredCount = batchResults.filter(item => item.outcome === 'DUPLICATE').length;
  const notAddedCount = batchResults.filter(
    item => item.outcome === 'NOT_ADDED' || item.outcome === 'FAILED'
  ).length;

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent} onClick={(event) => event.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Check for Additional Causes</h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: 0 }}>
            Review additional service information for other relevant possible causes.
          </p>
        </div>

        {loading && (
          <div
            style={{
              padding: '1rem',
              border: '1px solid #d9e2e3',
              borderRadius: '0.5rem',
              backgroundColor: '#f8faf9',
              color: '#173f42'
            }}
          >
            Checking for additional evidence-supported causes...
          </div>
        )}

        {!loading && error && (
          <div style={{ ...styles.suggestionResult, ...styles.rejectedResult }}>
            <strong>Additional-cause check failed</strong>
            <p style={{ margin: '0.5rem 0 0' }}>{error}</p>
          </div>
        )}

        {!loading && !error && result && (
          <>
            {workflowError && (
              <div style={{ ...styles.suggestionResult, ...styles.rejectedResult, marginBottom: '0.75rem' }}>
                <strong>Final workflow optimization failed</strong>
                <p style={{ margin: '0.5rem 0 0' }}>{workflowError}</p>
              </div>
            )}
            <div
              style={{
                padding: '0.75rem 0.875rem',
                border: '1px solid #d9e2e3',
                borderRadius: '0.5rem',
                backgroundColor: '#f8faf9',
                color: '#173f42',
                marginBottom: candidates.length > 0 ? '0.75rem' : 0
              }}
            >
              {result.summary}
            </div>

            {(result.validationIssues?.length || 0) > 0 && (
              <div
                style={{
                  padding: '0.75rem 0.875rem',
                  border: '1px solid #e4cf9b',
                  borderRadius: '0.5rem',
                  backgroundColor: '#fff8e8',
                  color: '#6b5522',
                  marginBottom: '0.75rem'
                }}
              >
                <strong>Some generated suggestions were omitted</strong>
                <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.25rem' }}>
                  {(result.validationIssues || []).map((issue, index) => (
                    <li key={`${index}-${issue}`}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {!processing && batchResults.length > 0 && (
              <div
                style={{
                  padding: '0.75rem 0.875rem',
                  border: '1px solid #a7d5bd',
                  borderRadius: '0.5rem',
                  backgroundColor: '#edf8f1',
                  color: '#175c3a',
                  marginBottom: '0.75rem'
                }}
              >
                <strong>Cause review completed</strong>
                <div style={{ marginTop: '0.3rem' }}>
                  {addedCount} added with action plans
                  {coveredCount > 0 ? `, ${coveredCount} already covered` : ''}
                  {notAddedCount > 0 ? `, ${notAddedCount} not added` : ''}.
                  {optimizationComplete ? ' The combined troubleshooting workflow was optimized.' : ''}
                </div>
              </div>
            )}

            {candidates.length > 0 && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    marginBottom: '0.75rem'
                  }}
                >
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton, width: 'auto' }}
                      onClick={onSelectAll}
                      disabled={processing}
                    >
                      Select All
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton, width: 'auto' }}
                      onClick={onClearSelection}
                      disabled={processing || selectedCandidateIds.length === 0}
                    >
                      Clear
                    </button>
                  </div>
                  <button
                    style={{ ...styles.button, ...styles.primaryButton, width: 'auto' }}
                    onClick={onAddSelected}
                    disabled={processing || selectedCandidateIds.length === 0}
                  >
                    {processing
                      ? optimizing
                        ? 'Optimizing Workflow...'
                        : 'Creating Action Plans...'
                      : `Add Selected Causes (${selectedCandidateIds.length})`}
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {candidates.map(candidate => {
                    const candidateResult = resultByCandidateId.get(candidate.id);
                    const completed = Boolean(candidateResult && candidateResult.outcome !== 'FAILED');
                    return (
                      <div
                        key={candidate.id}
                        style={{
                          padding: '0.875rem',
                          border: completed ? '1px solid #b8c8ca' : '1px solid #e4cf9b',
                          borderRadius: '0.5rem',
                          backgroundColor: completed ? '#f7f9f9' : '#fff8e8'
                        }}
                      >
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.65rem',
                            cursor: processing || completed ? 'default' : 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(candidate.id)}
                            disabled={processing || completed}
                            onChange={() => onToggleCandidate(candidate.id)}
                            style={{ marginTop: '0.2rem' }}
                          />
                          <span style={{ flex: 1, fontWeight: 700, color: '#173f42' }}>
                            {candidate.shortTitle}
                          </span>
                        </label>
                        <p style={{ margin: '0.35rem 0 0', color: '#374151' }}>{candidate.description}</p>
                        <p style={{ margin: '0.5rem 0 0', color: '#6b5522', fontSize: '0.875rem' }}>
                          {candidate.reasoningSummary}
                        </p>
                        {processingCandidateId === candidate.id && (
                          <div style={{ marginTop: '0.65rem', fontWeight: 600, color: '#6b5522' }}>
                            Validating cause and creating its action plan...
                          </div>
                        )}
                        {candidateResult && (
                          <div
                            style={{
                              marginTop: '0.65rem',
                              fontWeight: 600,
                              color:
                                candidateResult.outcome === 'ADDED'
                                  ? '#175c3a'
                                  : candidateResult.outcome === 'DUPLICATE'
                                  ? '#245d46'
                                  : '#9b2c2c'
                            }}
                          >
                            {candidateResult.message}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        <div style={styles.buttonRow}>
          {!loading && error && (
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={onRetry}
              disabled={processing}
            >
              Try Again
            </button>
          )}
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={onClose}
            disabled={processing}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
